import type { PetAttribute, PetEvolutionStage, PetStatusEffect } from '.';
import type { State } from 'src/replicant/State';
import { clamp } from '../utils';
import { duration } from '../utils/duration';
import * as ruleset from './pet.ruleset';
import { evolutions, PetEvolution, PetSpeciesID } from './pet.ruleset';
import { weightedRandom } from 'src/replicant/utils/random';
import { cleanPetName } from 'src/replicant/utils/string';
import * as Fate from '../fate';

/**
 * Get pet age, one real day == one year of pet age
 */
export const getPetAge = (state: State, now: number): number => {
  const { getCurrentPetState, getStatusEffectStartTime } = Fate.getters;
  const isDead = getCurrentPetState(state, now) === 'dead';

  const since = isDead ? getStatusEffectStartTime(state, 'dead', now) : now;

  return (
    Math.floor((since - getPetCreatedAt(state)) / duration({ days: 1 })) || 0
  );
};

export const getAttributeValues = (
  state: State,
  now: number,
): Record<PetAttribute, number> => {
  const res = {} as Record<PetAttribute, number>;

  for (const attribute in state.pet.attributes) {
    res[attribute as PetAttribute] = getAttributeValue(
      state,
      attribute as PetAttribute,
      now,
    );
  }

  return res;
};

export const getStressValue = (
  state: State,
  now: number,
  attributes?: Record<'dirtiness' | 'love' | 'stomach' | 'urine', number>,
) =>
  getPetCreatedAt(state)
    ? ruleset.getStressValue(
        attributes || {
          dirtiness: getAttributeValue(state, 'dirtiness', now),
          love: getAttributeValue(state, 'love', now),
          stomach: getAttributeValue(state, 'stomach', now),
          urine: getAttributeValue(state, 'urine', now),
        },
      )
    : 0;
/**
 * Get next reborn species after death
 */
export function getNextRebornSpecies(
  state: State,
  random: () => number,
): Extract<PetEvolution, { stage: 'baby' }> {
  // Get current evolution stage
  const { species } = getCurrentEvolution(state)!;

  const evolutionsForBucket = evolutions[getPetSpeciesBucket(state)];

  // Get next possible evolutions weights and species
  const nextEvolutions: Partial<Record<PetSpeciesID, number>> =
    evolutionsForBucket[species].reborn_weights;

  const ids = Object.keys(nextEvolutions) as PetSpeciesID[];

  // Transform data for weightedRandom
  const evolutionsArray = ids.map((item) => {
    return { chance: nextEvolutions[item]!, species: item };
  });

  const weights = evolutionsArray.map((item) => {
    return item.chance;
  });

  // Roll and detect species
  const index = weightedRandom(weights, random);
  const id = evolutionsArray[index].species;

  return evolutionsForBucket[id] as Extract<PetEvolution, { stage: 'baby' }>;
}

export const getResolutionAttempts = (
  state: State,
  statusEffect: PetStatusEffect,
) => state.pet.statusEffects[statusEffect]?.resolutionAttempts;

/**
 * Get pet name
 */
export const getPetName = (state: State) => cleanPetName(state.pet.name);

/**
 * Get next evolution species for pet
 */
export function getNextSpeciesEvolution(
  state: State,
  random: () => number,
): { id: PetSpeciesID; stage: PetEvolutionStage } | null {
  // Get current evolution stage
  const { stage, species } = getCurrentEvolution(state)!;

  if (stage === 'adult') {
    // Adults do not evolve in control.
    return null;
  }

  // Transform data for weightedRandom
  const evolutionsArray = getNextSpecies(getPetSpeciesBucket(state), species);

  const weights = evolutionsArray.map((item) => {
    return item.chance;
  });

  // Roll and detect species
  const index = weightedRandom(weights, random);
  const id = evolutionsArray[index].species;
  const resultStage = evolutions[getPetSpeciesBucket(state)][id].stage;

  return { id, stage: resultStage };
}

export const getAnalyticsAttributeUserProperties = (
  state: State,
  now: number,
) =>
  getPetCreatedAt(state)
    ? {
        stomach: getAttributeValue(state, 'stomach', now),
        love: getAttributeValue(state, 'love', now),
        dirtiness: getAttributeValue(state, 'dirtiness', now),
        urine: getAttributeValue(state, 'urine', now),
        stress: getStressValue(state, now),
        tooth_acidity: getAttributeValue(state, 'toothAcidity', now),
      }
    : {};

export const getAnalyticsUserProperties = (state: State, now: number) =>
  getPetCreatedAt(state)
    ? {
        // Current pet name.
        pet_name: state.pet.name, // Get plain name, do not use getter, since that may sanitize it.

        // Growth stage of the current pet
        stage: getCurrentEvolution(state)?.stage,

        // Number of times the player has finished raising pets (= number of deaths or marriages)
        pets_raised: state.petRaised,

        // Current pet species.
        pet_id: getCurrentEvolution(state)!.species,

        // Current pet state.
        pet_state: Fate.getters.getCurrentPetState(state, now),

        // Current pet age.
        age: getPetAge(state, now),
      }
    : {
        pets_raised: 0,
      };

export const getCurrentEvolution = ({ pet }: State) => pet.evolutions?.at(-1);

export function getEquippedPetSpecies(state: State): PetSpeciesID | undefined {
  return getCurrentEvolution(state)?.species;
}

export const getPetCreatedAt = ({ pet }: State) => pet.evolutions[0]?.timestamp;

/**
 * Get array of next available evolutions
 *
 * @return [{ chance: number,  species: 'ball' | 'dog'... }, ...];
 */
function getNextSpecies(
  petSpeciesBucket: ruleset.PetSpeciesBucket,
  currentSpeciesID: PetSpeciesID,
): Array<{ chance: number; species: PetSpeciesID }> {
  // Get next possible evolutions weights and species
  const nextEvolutions: Partial<Record<PetSpeciesID, number>> =
    evolutions[petSpeciesBucket][currentSpeciesID].next_weights;

  const ids = Object.keys(nextEvolutions) as PetSpeciesID[];

  // Transform data for weightedRandom
  const evolutionsArray = ids.map((item) => {
    return { chance: nextEvolutions[item]!, species: item };
  });

  return evolutionsArray;
}

/**
 * Return decay value for current species attribute
 */
export function getPetAttributeDecayInterval(
  state: State,
  attribute: PetAttribute,
): number {
  const currentEvolution = getCurrentEvolution(state);
  if (!currentEvolution) {
    return 0;
  }

  const decayIntervals = ruleset.decayIntervals[getPetSpeciesBucket(state)];
  const { species } = currentEvolution;

  return decayIntervals[species][attribute];
}

/**
 * Pet attributes interpolation
 **/
export function getAttributeValue(
  state: State,
  attribute: PetAttribute,
  now: number,
): number {
  const currentEvolution = getCurrentEvolution(state);
  if (!currentEvolution) {
    return 0;
  }

  const { attributeBounds, decayMultipliers } = ruleset;

  const { value, lastUpdatedAt } = state.pet.attributes[attribute];

  const decayInterval = getPetAttributeDecayInterval(state, attribute);

  const interval = Fate.getters.getAwakeDuration(state, lastUpdatedAt, now);

  const decayAmount = interval / decayInterval;
  const { min } = attributeBounds;
  const decayStep = decayMultipliers[attribute];

  // Calculate decay.
  let decay = decayStep * decayAmount;

  return clamp(value + decay, min, getMaxPetAttributeValue());
}

/**
 * Get maximum value for pet attribute
 */
export function getMaxPetAttributeValue(): number {
  return ruleset.attributeBounds.max;
}

/**
 * Get time to evolve.
 */
export function getTimeToEvolve(_: State, stage: PetEvolutionStage): number {
  if (stage === 'adult') {
    return Number.MAX_SAFE_INTEGER;
  }

  return ruleset.timeToEvolve[stage];
}

/**
 * Check if pet is new baby.
 * @param state
 * @param now
 * @returns
 */
export function isInitialPet(state: State): boolean {
  const { stage } = getCurrentEvolution(state)!;
  return stage === 'baby' && state.history.length === 0;
}

/**
 * Get pet species config from bucket.
 * @param bucket
 * @returns
 */
export function getPetSpecies(state: State) {
  return ruleset.getPetSpeciesById(getPetSpeciesBucket(state));
}

/**
 * Get pet species bucket.
 * @param state
 * @returns
 */
export function getPetSpeciesBucket(_: State): ruleset.PetSpeciesBucket {
  return 'control';
}

/**
 * Get the gem cost for the next redo
 */
export function getEvolutionRedoCost(state: State): number {
  const costs = ruleset.redoEvolutionCost;

  const costIndex = Math.min(
    state.evolutionRewind?.count ?? 0,
    costs.length - 1,
  );

  return costs[costIndex];
}

/**
 * Get next evolution species for pet with redo
 */
export function getNextRedoSpeciesEvolution(
  state: State,
  random: () => number,
): { id: PetSpeciesID; stage: PetEvolutionStage } | null {
  // Get current evolution stage
  const evolution = getCurrentEvolution(state);

  // No evolution, no redo.
  if (!evolution) {
    return null;
  }

  // Adults do not evolve.
  const { stage, species } = evolution;
  if (stage === 'adult') {
    return null;
  }

  // Transform data for weightedRandom
  const speciesBucket = getPetSpeciesBucket(state);
  const evolutionsArray = getNextSpecies(speciesBucket, species);

  // All of the evolutions should be the same next stage
  const resultStage =
    evolutions[speciesBucket][evolutionsArray[0].species].stage;

  // The total weight of the regular evolutions
  const totalWeight = evolutionsArray.reduce((acc, a) => acc + a.chance, 0);

  // The ids of the default evolutions
  const defaultSpecies = evolutionsArray.map((evolution) => evolution.species);

  // Find all the pets in the same stage without the default evolutions
  const remainingSpecies = getPetSpecies(state).filter(
    (species) =>
      species.stage === resultStage && !defaultSpecies.includes(species.id),
  );

  // The weight assigned to the rest of the species
  const assignedWeight = totalWeight / 10 / remainingSpecies.length;

  // Add them to the possible evolutions
  remainingSpecies.forEach((species) => {
    evolutionsArray.push({ species: species.id, chance: assignedWeight });
  });

  const weights = evolutionsArray.map((item) => {
    // Ignore previous rerolled species.
    if (item.species === (state.evolutionRewind!.species as PetSpeciesID)) {
      return 0;
    }

    return item.chance;
  });

  // Roll and detect species
  const index = weightedRandom(weights, random);
  const id = evolutionsArray[index].species;

  return { id, stage: resultStage };
}

/**
 * Get config for a given species.
 * @param state
 * @param species
 * @returns
 */
export function getPetSpeciesConfig(state: State, species: PetSpeciesID) {
  return ruleset.PetEvolutionMap[getPetSpeciesBucket(state)][species];
}

/**
 * Get pet species name
 * @param state
 * @param species
 * @returns
 */
export function getPetSpeciesName(
  state: State,
  species: PetSpeciesID = getCurrentEvolution(state)?.species ?? 'dog',
): string {
  return getPetSpeciesConfig(state, species)[
    `name_${state.settings.locale === 'ja' ? 'jp' : 'en'}`
  ];
}
